import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { formatPriceMwk, getPlanById } from "../../config/subscriptionPlans";
import { loadPaychanguPopupScript, openPaychanguInlineCheckout } from "../../utils/paychanguInline";
import {
    abandonSubscriptionPaymentCheckout,
    confirmSubscriptionPaymentCheckout,
    fetchSubscriptionPaymentStatus,
    initiateSubscriptionPaymentCheckout,
    normalizePaymentSession,
    paymentSessionErrorFromResponse,
} from "../../utils/subscriptionPaymentApi";

const POLL_MS = 4000;

function usePaymentDialogStyles() {
    const theme = useTheme();

    return {
        dialogPaperSx: {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            backgroundImage: "none",
            border: `1px solid ${theme.palette.divider}`,
        },
        primaryButtonSx: {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            fontWeight: 700,
            "&:hover": { bgcolor: theme.palette.primary.dark },
        },
    };
}

function extractErrorMessage(error) {
    const data = error?.response?.data;
    const rawMsg = String(data?.message || data?.error || error?.message || "");

    if (/email and password are required/i.test(rawMsg)) {
        return paymentSessionErrorFromResponse(
            data && typeof data === "object" ? data : { message: rawMsg },
        );
    }
    if (error?.isStaleAdminRegPayment || /email already exists|school name already exists/i.test(rawMsg)) {
        return paymentSessionErrorFromResponse({ message: rawMsg });
    }

    if (typeof data === "string" && data.trim()) return data.trim();
    if (data?.error) return String(data.error);
    if (data && typeof data === "object") {
        const sessionMsg = paymentSessionErrorFromResponse(data);
        if (sessionMsg && !data.publicKey) return sessionMsg;
    }
    if (data?.message) return String(data.message);
    if (error?.response?.status === 403) {
        return (
            "Payment access denied (403). Redeploy the AdminReg Edge Function in Supabase " +
            "(Dashboard → Edge Functions → AdminReg → Deploy) so subscription payments are allowed. " +
            "Or host the Express backend and set REACT_APP_PAYMENT_API_URL on Netlify."
        );
    }
    if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
        return "Network error. Check your connection and that the payment backend is running.";
    }
    return error?.message || "Could not start subscription payment";
}

export default function SubscriptionPaymentDialog({
    open,
    onClose,
    onPaymentConfirmed,
    adminId,
    planId,
    schoolName,
    adminEmail,
    adminName,
}) {
    const navigate = useNavigate();
    const plan = getPlanById(planId);
    const amountMwk = plan.priceMwk;
    const styles = usePaymentDialogStyles();

    const [loading, setLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [completed, setCompleted] = useState(null);
    const [error, setError] = useState("");
    const pollRef = useRef(null);
    const pendingRef = useRef("");

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const resetDialog = useCallback(() => {
        setLoading(false);
        setConfirming(false);
        setCompleted(null);
        setError("");
        pendingRef.current = "";
        stopPolling();
    }, [stopPolling]);

    useEffect(() => {
        if (open) {
            resetDialog();
            loadPaychanguPopupScript().catch(() => {
                /* script loads again on pay click */
            });
        }
        return () => stopPolling();
    }, [open, resetDialog, stopPolling]);

    const pollPaymentStatus = useCallback(async () => {
        const ref = pendingRef.current;
        if (!ref) return;

        try {
            const { data } = await fetchSubscriptionPaymentStatus(ref);

            if (data?.status === "Completed") {
                stopPolling();
                setConfirming(false);
                setLoading(false);
                setCompleted(data);
                onPaymentConfirmed?.(data);
            } else if (data?.status === "Expired") {
                stopPolling();
                setConfirming(false);
                setLoading(false);
                setError("Payment window expired. Close this dialog and try again.");
            }
        } catch {
            /* keep polling while popup is open */
        }
    }, [onPaymentConfirmed, stopPolling]);

    const startPolling = useCallback(
        (referenceNumber) => {
            pendingRef.current = referenceNumber;
            setConfirming(true);
            pollPaymentStatus();
            pollRef.current = setInterval(pollPaymentStatus, POLL_MS);
        },
        [pollPaymentStatus],
    );

    const handlePayWithMobileMoney = async () => {
        if (!adminId) {
            setError("School account not found. Sign in again.");
            return;
        }

        setLoading(true);
        setError("");

        let referenceNumber = "";

        try {
            const { data } = await initiateSubscriptionPaymentCheckout({
                adminId,
                planId: plan.id,
                schoolName,
                adminEmail,
                adminName,
                amount: amountMwk,
                returnOrigin: window.location.origin,
            });

            const session = normalizePaymentSession(data);
            if (!session) {
                throw new Error(paymentSessionErrorFromResponse(data));
            }

            referenceNumber = session.referenceNumber;
            await openPaychanguInlineCheckout(session);
            await confirmSubscriptionPaymentCheckout(referenceNumber);
            startPolling(referenceNumber);
            setLoading(false);
        } catch (err) {
            if (referenceNumber) {
                abandonSubscriptionPaymentCheckout(referenceNumber).catch(() => {});
            }
            setError(extractErrorMessage(err));
            setLoading(false);
            setConfirming(false);
            stopPolling();
        }
    };

    const handleClose = () => {
        if (!loading && !confirming) {
            const ref = pendingRef.current;
            stopPolling();
            if (ref && !completed) {
                abandonSubscriptionPaymentCheckout(ref).catch(() => {});
            }
            pendingRef.current = "";
            onClose();
        }
    };

    const handleContinue = () => {
        stopPolling();
        onClose();
    };

    const showSignInAfterStalePayment =
        Boolean(error) &&
        (/sign in with the email/i.test(error) || /blocked payment/i.test(error));

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: styles.dialogPaperSx }}
        >
            <DialogTitle sx={{ color: "text.primary", pb: 1 }}>
                Pay subscription via mobile money
            </DialogTitle>

            <DialogContent>
                {error ? (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {completed ? (
                    <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "success.main" }} />
                        <Typography variant="h6" fontWeight={800} textAlign="center">
                            Payment confirmed
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Your subscription is now active.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Amount received:{" "}
                            {formatPriceMwk(completed.amountReceived ?? completed.amount ?? amountMwk)}
                        </Typography>
                    </Stack>
                ) : (
                    <Stack spacing={2.5}>
                        <Typography variant="body2" color="text.secondary">
                            Activate <strong>{schoolName || "your school"}</strong> on the{" "}
                            <strong>{plan.name}</strong> plan ({plan.billingLabel}).
                        </Typography>

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: (t) => `1px solid ${t.palette.divider}`,
                                bgcolor: (t) =>
                                    t.palette.mode === "light"
                                        ? "rgba(0,0,0,0.04)"
                                        : "rgba(255,255,255,0.06)",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Amount due
                            </Typography>
                            <Typography variant="h4" fontWeight={800} color="primary.main">
                                {formatPriceMwk(amountMwk)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {plan.studentRange}
                            </Typography>
                        </Box>

                        {confirming ? (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Complete payment in the PayChangu popup. This dialog will update
                                automatically when your payment is confirmed.
                            </Alert>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                A secure PayChangu popup will open on this page. Choose{" "}
                                <strong>Airtel Money</strong>, <strong>TNM Mpamba</strong>, card, or
                                bank, then complete payment on your phone (PIN stays on your wallet app).
                            </Typography>
                        )}

                        {!confirming ? (
                            <Button
                                variant="contained"
                                size="large"
                                disabled={loading}
                                onClick={handlePayWithMobileMoney}
                                startIcon={loading ? null : <PhoneAndroidIcon />}
                                sx={styles.primaryButtonSx}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Pay with mobile money"
                                )}
                            </Button>
                        ) : (
                            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                                <CircularProgress size={22} />
                                <Typography variant="body2" color="text.secondary">
                                    Waiting for payment confirmation…
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: "wrap", gap: 1 }}>
                {showSignInAfterStalePayment ? (
                    <Button
                        variant="contained"
                        onClick={() => navigate("/login")}
                        sx={styles.primaryButtonSx}
                    >
                        Sign in to continue
                    </Button>
                ) : null}
                {completed ? (
                    <Button variant="contained" onClick={handleContinue} sx={styles.primaryButtonSx}>
                        Continue
                    </Button>
                ) : (
                    <Button onClick={handleClose} color="inherit" disabled={loading || confirming}>
                        Cancel
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
